"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const DOI_REGEX = /10\.\d{4,9}\/[^\s]+/;
const TRAILING_PUNCTUATION_REGEX = /[.,;:)}\]]+$/;

interface DoiResult {
  input: string;
  doi: string | null;
  valid: boolean;
}

function extractDoi(input: string): string | null {
  const trimmed = input.trim();
  const match = trimmed.match(DOI_REGEX);
  if (match) {
    // Remove trailing punctuation that might be part of a sentence
    return match[0].replace(TRAILING_PUNCTUATION_REGEX, "");
  }
  return null;
}

function validateSingle(input: string): DoiResult {
  const doi = extractDoi(input);
  return {
    input: input.trim(),
    doi,
    valid: doi !== null,
  };
}

/** English strings. A translated content file overrides these through `ui`. */
export const DOI_VALIDATOR_STRINGS = {
  singleDoiValidation: "Single DOI Validation",
  doiToValidate: "DOI to validate",
  eG101037Edu0000812:
    "e.g. 10.1037/edu0000812 or https://doi.org/10.1037/edu0000812",
  validate: "Validate",
  validDoiFormat: "Valid DOI format",
  invalidDoiFormat: "Invalid DOI format",
  formattedDoi: "Formatted DOI",
  copied: "Copied!",
  copy: "Copy",
  resolveDoi: "Resolve DOI →",
  opensInANewTab: "Opens in a new tab",
  couldNotFindAValid:
    "Could not find a valid DOI pattern (10.xxxx/xxxx) in your input. Make sure the DOI starts with “10.” followed by a registrant code and suffix.",
  batchValidation: "Batch Validation",
  pasteMultipleDoisOnePer: "Paste multiple DOIs (one per line)",
  validateAll: "Validate All",
  batchSummary:
    "one: {valid} valid, {invalid} invalid out of {count} DOI|other: {valid} valid, {invalid} invalid out of {count} DOIs",
  resolve: "Resolve",
  whatIsADoi: "What is a DOI?",
  aDigitalObjectIdentifierDoi:
    "A Digital Object Identifier (DOI) is a persistent identifier used to uniquely identify academic articles, datasets, and other scholarly works. DOIs always start with “10.” followed by a registrant code and a suffix separated by a slash.",
  doisAreRequiredInApa:
    "DOIs are required in APA 7th edition references and recommended in MLA and Chicago citations when available. They provide a permanent link to the source, even if the URL changes.",
  copyFailedToast: "Couldn't copy to clipboard. Please copy manually.",
  formatAsUrl: "As URL",
  formatAsDoiLower: "As doi:",
  formatAsDoiUpper: "As DOI:",
  formatRaw: "Raw",
} as const;

export default function DoiValidatorWidget() {
  const [singleInput, setSingleInput] = useState("");
  const [singleResult, setSingleResult] = useState<DoiResult | null>(null);
  const [batchInput, setBatchInput] = useState("");
  const [batchResults, setBatchResults] = useState<DoiResult[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  function handleValidateSingle() {
    if (!singleInput.trim()) {
      setSingleResult(null);
      return;
    }
    setSingleResult(validateSingle(singleInput));
  }

  function handleValidateBatch() {
    const lines = batchInput
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    setBatchResults(lines.map(validateSingle));
  }

  async function copyToClipboard(text: string, key: string) {
    if (await writeToClipboard(text)) {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.error(strings.copyFailedToast);
    }
  }

  const strings = useToolUi(DOI_VALIDATOR_STRINGS);
  const locale = useToolLocale();

  return (
    <>
      {/* Single DOI */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.singleDoiValidation}
        </h2>
        <div className="flex gap-3">
          <input
            aria-label={strings.doiToValidate}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
            onChange={(e) => setSingleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleValidateSingle();
              }
            }}
            placeholder={strings.eG101037Edu0000812}
            type="text"
            value={singleInput}
          />
          <button
            className="rounded-lg bg-foreground px-6 py-2 font-medium text-background text-sm transition-colors hover:bg-foreground/90"
            onClick={handleValidateSingle}
            type="button"
          >
            {strings.validate}
          </button>
        </div>

        {singleResult && (
          <div className="space-y-4 rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-3 w-3 rounded-full ${singleResult.valid ? "bg-green-500" : "bg-red-500"}`}
              />
              <span className="font-medium text-foreground text-sm">
                {singleResult.valid
                  ? strings.validDoiFormat
                  : strings.invalidDoiFormat}
              </span>
            </div>

            {singleResult.valid && singleResult.doi && (
              <div className="space-y-3 text-sm">
                <div className="space-y-2">
                  <h3 className="font-medium text-foreground">
                    {strings.formattedDoi}
                  </h3>
                  <div className="space-y-1.5">
                    {[
                      {
                        label: strings.formatAsUrl,
                        value: `https://doi.org/${singleResult.doi}`,
                        key: "url",
                      },
                      {
                        label: strings.formatAsDoiLower,
                        value: `doi:${singleResult.doi}`,
                        key: "doi",
                      },
                      {
                        label: strings.formatAsDoiUpper,
                        value: `DOI: ${singleResult.doi}`,
                        key: "DOI",
                      },
                      {
                        label: strings.formatRaw,
                        value: singleResult.doi,
                        key: "raw",
                      },
                    ].map((fmt) => (
                      <div
                        className="flex items-center justify-between gap-2 rounded border border-border bg-muted/30 px-3 py-2"
                        key={fmt.key}
                      >
                        <div>
                          <span className="text-muted-foreground">
                            {fmt.label}:{" "}
                          </span>
                          <code className="text-foreground">{fmt.value}</code>
                        </div>
                        <button
                          className="shrink-0 rounded px-2 py-1 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
                          onClick={() => copyToClipboard(fmt.value, fmt.key)}
                          type="button"
                        >
                          {copied === fmt.key ? strings.copied : strings.copy}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <a
                    className="inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-4 hover:text-foreground/80"
                    href={`https://doi.org/${singleResult.doi}`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {strings.resolveDoi}
                  </a>
                  <span className="ml-2 text-muted-foreground text-xs">
                    {strings.opensInANewTab}
                  </span>
                </div>
              </div>
            )}

            {!singleResult.valid && (
              <p className="text-muted-foreground text-sm">
                {strings.couldNotFindAValid}
              </p>
            )}
          </div>
        )}
      </section>
      {/* Batch mode */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.batchValidation}
        </h2>
        <div className="space-y-3">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="batch-input"
          >
            {strings.pasteMultipleDoisOnePer}
          </label>
          <textarea
            className="min-h-[150px] w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
            id="batch-input"
            onChange={(e) => setBatchInput(e.target.value)}
            placeholder={
              "10.1037/edu0000812\nhttps://doi.org/10.1000/xyz123\nnot-a-doi\n10.1126/science.abc1234"
            }
            value={batchInput}
          />
          <button
            className="rounded-lg bg-foreground px-6 py-2 font-medium text-background text-sm transition-colors hover:bg-foreground/90"
            onClick={handleValidateBatch}
            type="button"
          >
            {strings.validateAll}
          </button>
        </div>

        {batchResults.length > 0 && (
          <div className="space-y-2">
            <div className="text-muted-foreground text-sm">
              {tPlural(strings.batchSummary, locale, batchResults.length, {
                valid: batchResults.filter((r) => r.valid).length,
                invalid: batchResults.filter((r) => !r.valid).length,
              })}
            </div>
            <div className="space-y-1.5">
              {batchResults.map((result, i) => (
                <div
                  className="flex items-center gap-3 rounded border border-border bg-card px-4 py-2 text-sm"
                  key={`batch-${
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable list order
                    i
                  }`}
                >
                  <span
                    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${result.valid ? "bg-green-500" : "bg-red-500"}`}
                  />
                  <span className="min-w-0 flex-1 truncate text-foreground">
                    {result.input}
                  </span>
                  {result.valid && result.doi && (
                    <a
                      className="shrink-0 text-muted-foreground text-xs underline-offset-4 hover:text-foreground hover:underline"
                      href={`https://doi.org/${result.doi}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {strings.resolve}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      {/* Info */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.whatIsADoi}
        </h2>
        <div className="space-y-2 text-muted-foreground text-sm leading-relaxed">
          <p>{strings.aDigitalObjectIdentifierDoi}</p>
          <p>{strings.doisAreRequiredInApa}</p>
        </div>
      </section>
      {/* Related tools */}
    </>
  );
}
